/**
 * Phase 1 — fixed-timestep game loop via requestAnimationFrame.
 * One tick per frame max (matches legacy setInterval semantics).
 */
import { useEffect, useRef } from 'react';
import { runTickFrame, type TickFrameDeps } from '../src/adapters/ui/tick/runTickFrame';
import {
  shouldRunTicks,
  stepTickAccumulator,
  tickIntervalMs,
  type GameLoopPauseReason,
} from './tickLoopUtils';

export type { GameLoopPauseReason } from './tickLoopUtils';
export { shouldRunTicks, stepTickAccumulator } from './tickLoopUtils';

export function useGameLoop(
  pauseReason: GameLoopPauseReason,
  tickDeps: TickFrameDeps
): void {
  const depsRef = useRef(tickDeps);
  depsRef.current = tickDeps;

  const pauseRef = useRef(pauseReason);
  pauseRef.current = pauseReason;

  useEffect(() => {
    let rafId = 0;
    let lastTime = performance.now();
    let accumulator = 0;

    const frame = (now: number) => {
      rafId = requestAnimationFrame(frame);

      const pause = pauseRef.current;
      if (!shouldRunTicks(pause)) {
        lastTime = now;
        accumulator = 0;
        return;
      }

      const delta = now - lastTime;
      lastTime = now;

      const tickMs = tickIntervalMs(pause.gameSpeed);
      const step = stepTickAccumulator(accumulator, delta, tickMs);
      accumulator = step.accumulator;
      if (step.shouldTick) {
        runTickFrame(depsRef.current);
      }
    };

    rafId = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, []);
}